/**
 * src/lib/inflightInterviewPrepRecovery.js — RC 1.52 Phase 3B.3
 * (operator-locked, 2026-05-11 PM).
 *
 * Mirror of `inflightLetterRecovery.js` for Interview Prep. Closes
 * the remount-rebind UX gap so a section unmount during generation
 * surfaces the recovered prep automatically.
 *
 * Marker shape:
 *   {
 *     submittedAtMs:    number,
 *     idempotencyKey:   string,        // stable per (user, job, source)
 *     runId:            string|null,   // set when async_202 returns
 *     jobId:            string,
 *     sourceCvId:       string|null,
 *     startedAt:        ISO string,
 *   }
 *
 * Hard rails:
 *   • No new endpoint. No schema change. No billing change.
 *   • Marker is cleared on confirmed success so a retry can't
 *     reuse a stale runId for a different intent.
 *   • TTL 10 min (matches `IDEMPOTENCY_TTL_MS`).
 *   • Owner-scoped: keyed by `(userId, jobId)` so different users
 *     / jobs never see each other's markers.
 */

export const INFLIGHT_INTERVIEW_PREP_WINDOW_MS = 10 * 60 * 1000;

const KEY_PREFIX = 'inflight-interview-prep';

function _key(userId, jobId) {
  return `${KEY_PREFIX}:${userId || ''}:${jobId || ''}`;
}

export function writeInflightInterviewPrepMarker(userId, jobId, {
  idempotencyKey, runId, sourceCvId,
} = {}) {
  if (!jobId || !idempotencyKey) return;
  try {
    sessionStorage.setItem(
      _key(userId, jobId),
      JSON.stringify({
        submittedAtMs:  Date.now(),
        idempotencyKey,
        runId:          runId || null,
        jobId,
        sourceCvId:     sourceCvId || null,
        startedAt:      new Date().toISOString(),
      }),
    );
  } catch { /* sessionStorage unavailable — non-fatal */ }
}

/**
 * Update an existing marker with a runId (after async_202 returns).
 * No-op if no marker exists or runId is null.
 */
export function patchInflightInterviewPrepRunId(userId, jobId, runId) {
  if (!jobId || !runId) return;
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId));
    if (!raw) return;
    const m = JSON.parse(raw);
    if (!m || typeof m !== 'object') return;
    m.runId = runId;
    sessionStorage.setItem(_key(userId, jobId), JSON.stringify(m));
  } catch { /* noop */ }
}

export function readInflightInterviewPrepMarker(userId, jobId) {
  if (!jobId) return null;
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId));
    if (!raw) return null;
    const m = JSON.parse(raw);
    if (!m || typeof m !== 'object') return null;
    if (!m.idempotencyKey || !m.submittedAtMs) return null;
    return m;
  } catch { return null; }
}

export function clearInflightInterviewPrepMarker(userId, jobId) {
  if (!jobId) return;
  try { sessionStorage.removeItem(_key(userId, jobId)); } catch { /* */ }
}

export function isInflightInterviewPrepMarkerExpired(marker, nowMs = Date.now()) {
  if (!marker?.submittedAtMs) return true;
  return (nowMs - Number(marker.submittedAtMs)) > INFLIGHT_INTERVIEW_PREP_WINDOW_MS;
}
